/**
 * Keeps the field being typed in above the on-screen keyboard on phones.
 *
 * iOS does not shrink the layout viewport when the keyboard opens — only the
 * visual viewport — and every form here scrolls inside an inner box (a
 * drawer or the drawer content), not the window. The browser's own reveal
 * then gives up: the tapped field stays under the keyboard. So once the
 * keyboard has settled, the field's nearest scroll box is moved to put the
 * field in the upper third of what is still visible, below the floating
 * navbar.
 *
 * While the keyboard is up, `--pc-keyboard-inset` on <html> holds its height
 * and <body> carries `pc-keyboard-open`, so the form can add room at its end
 * (see `.pc-form-footer` in uikit.css) and its last fields can rise too.
 *
 * Returns a function that removes the listeners.
 */
export function keepFocusedFieldVisible(document: Document): () => void {
  const view = document.defaultView;
  const viewport = view?.visualViewport;
  if (!view || !viewport || view.matchMedia('(hover: hover)').matches) {
    return () => {};
  }

  /** The floating navbar (4rem at the 14.4px root) plus a little air. */
  const TOP_CLEARANCE = 72;
  const BOTTOM_CLEARANCE = 16;
  /** Below this the viewport loss is browser chrome, not a keyboard. */
  const MIN_KEYBOARD_HEIGHT = 120;
  /** The keyboard's slide-in, for when no viewport resize follows the focus
   *  (the keyboard was already up and focus moved to the next field). */
  const SETTLE_DELAY_MS = 350;

  let settleTimer: ReturnType<typeof setTimeout> | null = null;

  const keyboardHeight = (): number =>
    Math.max(0, view.innerHeight - viewport.height - viewport.offsetTop);

  const syncKeyboardState = (): void => {
    const height = keyboardHeight();
    const open = height >= MIN_KEYBOARD_HEIGHT && isTextEntry(document.activeElement);
    document.body.classList.toggle('pc-keyboard-open', open);
    document.documentElement.style.setProperty('--pc-keyboard-inset', open ? `${height}px` : '0px');
  };

  const revealFocusedField = (): void => {
    syncKeyboardState();
    const field = document.activeElement;
    if (!isTextEntry(field) || keyboardHeight() < MIN_KEYBOARD_HEIGHT) {
      return;
    }
    const visibleTop = viewport.offsetTop + TOP_CLEARANCE;
    const visibleBottom = viewport.offsetTop + viewport.height - BOTTOM_CLEARANCE;
    const rect = field.getBoundingClientRect();
    if (rect.top >= visibleTop && rect.bottom <= visibleBottom) {
      return;
    }
    const target = visibleTop + (visibleBottom - visibleTop) / 4;
    const scroller = nearestScrollBox(field, view);
    if (scroller) {
      scroller.scrollBy({ top: rect.top - target, behavior: 'smooth' });
    }
  };

  const scheduleReveal = (): void => {
    if (settleTimer) {
      clearTimeout(settleTimer);
    }
    settleTimer = setTimeout(revealFocusedField, SETTLE_DELAY_MS);
  };

  const onFocusIn = (event: FocusEvent): void => {
    if (isTextEntry(event.target as Element)) {
      scheduleReveal();
    }
  };

  // The keyboard closing (or the next field taking focus) must drop the room
  // added at the form's end, or the form keeps a keyboard-sized gap.
  const onFocusOut = (): void => {
    setTimeout(syncKeyboardState, 0);
  };

  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
  viewport.addEventListener('resize', scheduleReveal);

  return () => {
    if (settleTimer) {
      clearTimeout(settleTimer);
    }
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('focusout', onFocusOut);
    viewport.removeEventListener('resize', scheduleReveal);
  };
}

/** Fields that raise the keyboard: text-like inputs, textareas, editors. */
function isTextEntry(element: Element | null): element is HTMLElement {
  if (!element) {
    return false;
  }
  if (element instanceof HTMLTextAreaElement) {
    return true;
  }
  if (element instanceof HTMLInputElement) {
    const nonText = [
      'button',
      'checkbox',
      'color',
      'file',
      'hidden',
      'image',
      'radio',
      'range',
      'reset',
      'submit',
    ];
    return !nonText.includes(element.type);
  }
  return element instanceof HTMLElement && element.isContentEditable;
}

/** Closest ancestor that actually scrolls vertically. */
function nearestScrollBox(element: HTMLElement, view: Window): HTMLElement | null {
  let current = element.parentElement;
  while (current) {
    const overflowY = view.getComputedStyle(current).overflowY;
    const scrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (scrollable && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}
